/**
 * Must match `converterService.SUPPORTED_EXTENSIONS` in
 * `backend/modules/pdf-converter/services/converterService.py` (same members; order = sorted(set)).
 * `.pdf` is not in that set — backend passes PDF bytes through in `convert_upload_to_pdf_bytes`.
 */
const PDF_CONVERTER_SUPPORTED_EXTENSIONS = [
  '.docx',
  '.htm',
  '.html',
  '.jpeg',
  '.jpg',
  '.md',
  '.png',
  '.pptx',
  '.txt',
  '.xlsx',
];

const CONVERTIBLE_EXTENSIONS = ['.pdf', ...PDF_CONVERTER_SUPPORTED_EXTENSIONS];

/** True if AttachmentPreview / convert-for-preview can produce a PDF for this filename. */
export const isConvertible = (fileName) => {
  if (!fileName) return false;
  const i = fileName.lastIndexOf('.');
  const ext = i >= 0 ? fileName.slice(i).toLowerCase() : '';
  return CONVERTIBLE_EXTENSIONS.includes(ext);
};

/** PK\x03\x04 — plain ZIP and OOXML (docx/xlsx/pptx) share this; JAR/APk also ZIP-based. */
const ZIP_PK_SIGNATURE_MIMES = [
  'application/zip',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
];

const mimeTypeMap = {
  '.pdf': 'application/pdf',
  '.xls': 'application/vnd.ms-excel',
  '.xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  '.doc': 'application/msword',
  '.docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  '.pptx': 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  '.zip': 'application/zip',
  '.png': 'image/png',
  '.jpg': 'image/jpeg',
  '.jpeg': 'image/jpeg',
  '.tif': 'image/tiff',
  '.tiff': 'image/tiff',
  '.txt': 'text/plain'
};

export const validateFileType = async (file, allowedExtensions) => {
  const extension = `.${file.name.split('.').pop().toLowerCase()}`;
  if (!allowedExtensions.includes(extension)) {
    return false;
  }
  const expectedMimeType = mimeTypeMap[extension];
  if (expectedMimeType === undefined) {
    return true;
  }
  return new Promise((resolve) => {
    const fileReader = new FileReader();
    fileReader.onloadend = function() {
      const arr = new Uint8Array(fileReader.result).subarray(0, 4);
      let header = '';
      for (let i = 0; i < arr.length; i++) {
        header += arr[i].toString(16);
      }

      const signatures = {
        '89504e47': 'image/png',
        'ffd8ffe0': 'image/jpeg',
        'ffd8ffe1': 'image/jpeg',
        '25504446': 'application/pdf',
        '504b34': ZIP_PK_SIGNATURE_MIMES,
        'd0cf11e0': ['application/msword', 'application/vnd.ms-excel']
      };

      let isValidType = false;
      const detectedType = signatures[header];

      if (Array.isArray(detectedType)) {
        isValidType = detectedType.includes(expectedMimeType);
      } else {
        isValidType = detectedType === expectedMimeType;
      }

      if (extension === '.txt') {
        isValidType = true;
      }

      resolve(isValidType);
    };
    fileReader.readAsArrayBuffer(file.slice(0, 4));
  });
};
